/* ------------------------------------------------------------------ *
 * BJ-DS Mini Bag — recently viewed in the empty state.
 *
 * Figma "Shopify Final" 1109:53286 (variant "Default") puts a 2x2 grid of
 * DS product cards under the empty bag. The data is the same localStorage
 * record the PDP writes; the markup comes from sections/bj-bag-recently-
 * viewed.liquid through the Section Rendering API. assets/recently-viewed.js
 * is a shared file bound to `.recently-viewed-section`, so its machinery is
 * mirrored here rather than generalised in place.
 * ------------------------------------------------------------------ */
const BJ_BAG_RECENT_KEY = "__sf_theme_recently";

async function bjBagRenderRecentlyViewed(scope) {
	const box = (scope || document).querySelector("[data-bj-recent]");
	if (!box || box.dataset.bjRecentLoaded === "true") return;
	box.dataset.bjRecentLoaded = "true";

	let stored = [];
	try {
		stored = JSON.parse(localStorage.getItem(BJ_BAG_RECENT_KEY)) || [];
	} catch (e) {
		stored = [];
	}
	if (!Array.isArray(stored) || stored.length === 0) return;

	const days = Number(box.dataset.expirationDays) || 30;
	const ttl = days * 24 * 60 * 60 * 1000;
	const now = Date.now();
	const limit = Number(box.dataset.limit) || 4;

	const ids = stored
		.filter((item) => item && item.productId && now - item.timestamp < ttl)
		.map((item) => String(item.productId))
		.filter((id, index, all) => all.indexOf(id) === index)
		.slice(0, limit);
	if (ids.length === 0) return;

	const query = ids.map((id) => `id:${id}`).join("%20OR%20");

	try {
		const response = await fetch(`${box.dataset.baseUrl}&q=${query}`);
		if (!response.ok) return;
		const doc = new DOMParser().parseFromString(
			await response.text(),
			"text/html"
		);
		const source = doc.querySelector("[data-bj-recent-grid]");
		const target = box.querySelector("[data-bj-recent-grid]");
		if (!source || !target || source.children.length === 0) return;

		target.innerHTML = source.innerHTML;
		box.hidden = false;

		try {
			colorSwatches();
		} catch (e) {}
	} catch (e) {
		// No history panel is better than a broken one — stay hidden.
	}
}

class CartDrawer extends HTMLElement {
	constructor() {
		super();

		this.addEventListener(
			"keyup",
			(evt) => evt.code === "Escape" && this.close()
		);
		this.querySelector("#CartDrawer-Overlay").addEventListener(
			"click",
			this.close.bind(this)
		);
		this.setHeaderCartIconAccessibility();

		/* A cart change (remove, quantity) re-renders `.drawer__inner`, which
		   both wipes the recently-viewed grid and would strand the drawer in
		   the "ADDED TO BAG" view with no active line. cartUpdate is published
		   after that re-render lands. */
		if (
			typeof subscribe === "function" &&
			typeof PUB_SUB_EVENTS !== "undefined"
		) {
			subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
				const inner = this.querySelector("#CartDrawer");
				if (inner) inner.classList.remove("notification-open");
				bjBagRenderRecentlyViewed(this);
			});
		}
	}

	setHeaderCartIconAccessibility() {
		const cartLink = document.querySelector("#cart-icon-bubble");
		cartLink.setAttribute("role", "button");
		cartLink.setAttribute("aria-haspopup", "dialog");

		cartLink.addEventListener("click", (event) => {
			event.preventDefault();
			this.open(cartLink);
		});

		cartLink.addEventListener("keydown", (event) => {
			if (event.code.toUpperCase() === "SPACE") {
				event.preventDefault();
				this.open(cartLink);
			}
		});
	}

	/* UAT 2026-08-11 "the bag isn't consistent on open/close state when there
	   are multiple items". Two causes, both fixed here so a reopened drawer
	   renders exactly like a fresh page-load open:
	   1. close() only returns the drawer from "ADDED TO BAG" to the BAG (N)
	      list 1s after closing, on a timer. Reopening inside that second (or
	      adding another item) showed the confirmation view — and then the
	      stale timer flipped it to the list while the drawer was on screen.
	   2. The items well keeps its scrollTop between opens, so with enough
	      items to scroll, a reopened bag came back mid-list while a re-render
	      after add starts at the top.
	   Now every open that is NOT the add-to-bag confirmation shows the list
	   (renderContents flags its own open), any pending close timer is
	   cancelled, and the scroll wells go back to the top. */
	bjShowList() {
		const notification = this.querySelector(".notification");
		if (notification) notification.classList.remove("notification-open");
		this.querySelectorAll(".cart-item.active").forEach((item) =>
			item.classList.remove("active")
		);
	}

	bjResetScroll() {
		this.querySelectorAll(
			"cart-drawer-items, .drawer__contents, .drawer__inner-empty"
		).forEach((el) => {
			el.scrollTop = 0;
		});
	}

	open(triggeredBy) {
		if (triggeredBy) this.setActiveElement(triggeredBy);
		clearTimeout(this.bjCloseTimer);
		if (!this.bjOpeningFromAdd) this.bjShowList();
		this.bjOpeningFromAdd = false;
		this.bjResetScroll();
		bjBagRenderRecentlyViewed(this);
		const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
		if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
			this.setSummaryAccessibility(cartDrawerNote);

		// here the animation doesn't seem to always get triggered. A timeout seem to help
		setTimeout(() => {
			this.classList.add("animate", "active");
		});

		this.addEventListener(
			"transitionend",
			() => {
				const containerToTrapFocusOn = this.classList.contains("is-empty")
					? this.querySelector(".drawer__inner-empty")
					: document.getElementById("CartDrawer");
				const focusElement =
					this.querySelector(".drawer__inner") ||
					this.querySelector(".drawer__close");
				trapFocus(containerToTrapFocusOn, focusElement);
			},
			{ once: true }
		);

		document.body.classList.add("cart-overflow-hidden");

		if (!isSafari()) {
			document.body.classList.add("overflow-hidden");
		} else {
			document.body.classList.add("overflow-clip");
		}
	}

	close() {
		this.classList.remove("active");
		removeTrapFocus(this.activeElement);
		document.body.classList.remove("cart-overflow-hidden");

		if (!isSafari()) {
			document.body.classList.remove("overflow-hidden");
		} else {
			document.body.classList.remove("overflow-clip");
		}

		/* Back to the list once the slide-out has finished (the sheet's
		   transition is 0.7s), so the confirmation doesn't vanish mid-slide.
		   open() cancels this if the drawer comes back first. */
		clearTimeout(this.bjCloseTimer);
		this.bjCloseTimer = setTimeout(() => {
			this.bjShowList();
			this.bjResetScroll();
		}, 1000);
	}

	setSummaryAccessibility(cartDrawerNote) {
		cartDrawerNote.setAttribute("role", "button");
		cartDrawerNote.setAttribute("aria-expanded", "false");

		if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
			cartDrawerNote.setAttribute(
				"aria-controls",
				cartDrawerNote.nextElementSibling.id
			);
		}

		cartDrawerNote.addEventListener("click", (event) => {
			event.currentTarget.setAttribute(
				"aria-expanded",
				!event.currentTarget.closest("details").hasAttribute("open")
			);
		});

		cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
	}

	renderContents(parsedState) {
		this.querySelector(".drawer__inner").classList.contains("is-empty") &&
			this.querySelector(".drawer__inner").classList.remove("is-empty");
		this.productId = parsedState.id;
		this.getSectionsToRender().forEach((section) => {
			const sectionElement = section.selector
				? document.querySelector(section.selector)
				: document.getElementById(section.id);
			sectionElement.innerHTML = this.getSectionInnerHTML(
				parsedState.sections[section.id],
				section.selector
			);
		});

		let notification = this.querySelector(".notification");

		if (notification) {
			const activeItem = this.querySelector(
				`.cart-item[data-product-id="${this.productId}"`
			);
			/* No matching line (e.g. the response echoed an id the drawer does
			   not render) — fall back to the plain BAG (N) list rather than
			   opening an "ADDED TO BAG" panel with nothing in it. */
			if (activeItem) {
				notification.classList.add("notification-open");
				activeItem.classList.add("active");
			} else {
				notification.classList.remove("notification-open");
			}
		}

		bjBagRenderRecentlyViewed(this);

		/* The drawer may still be closing from a previous add — its timer
		   would otherwise strip the confirmation we just set up. */
		clearTimeout(this.bjCloseTimer);

		setTimeout(() => {
			this.querySelector("#CartDrawer-Overlay").addEventListener(
				"click",
				this.close.bind(this)
			);
			this.bjOpeningFromAdd = true;
			this.open();
		});
	}

	getSectionInnerHTML(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector).innerHTML;
	}

	getSectionsToRender() {
		return [
			{
				id: "cart-drawer",
				selector: "#CartDrawer",
			},
			{
				id: "cart-icon-bubble",
			},
		];
	}

	getSectionDOM(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector);
	}

	setActiveElement(element) {
		this.activeElement = element;
	}
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
	getSectionsToRender() {
		return [
			{
				id: "CartDrawer",
				section: "cart-drawer",
				selector: ".drawer__inner",
			},
			{
				id: "cart-icon-bubble",
				section: "cart-icon-bubble",
				selector: ".shopify-section",
			},
		];
	}
}

customElements.define("cart-drawer-items", CartDrawerItems);

if (!customElements.get("cart-note")) {
	customElements.define(
		"cart-note",
		class CartNote extends HTMLElement {
			constructor() {
				super();

				this.addEventListener(
					"input",
					debounce((event) => {
						const body = JSON.stringify({ note: event.target.value });
						fetch(`${routes.cart_update_url}`, {
							...fetchConfig(),
							...{ body },
						});
					}, ON_CHANGE_DEBOUNCE_TIMER)
				);
			}
		}
	);
}
/* QA-BAG (Dev QA sheet 2026-09-24): "Contact us" in the bag drawer and on the
   cart page is a real link to the contact page. When a global advisor-chat
   opener exists (window.bjOpenAdvisorChat), use it instead — progressive
   enhancement; without it the link simply navigates. Delegated so it survives
   the drawer's section re-renders. This file loads on every page (header). */
if (!window.bjContactLinkBound) {
	window.bjContactLinkBound = true;
	document.addEventListener("click", (event) => {
		const link = event.target.closest && event.target.closest("[data-bj-contact]");
		if (!link || typeof window.bjOpenAdvisorChat !== "function") return;
		if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
		try {
			const result = window.bjOpenAdvisorChat({ source: "bag" });
			if (result === false) return;
			event.preventDefault();
			const drawer = document.querySelector("cart-drawer");
			if (drawer && link.closest("cart-drawer") && typeof drawer.close === "function") drawer.close();
		} catch (e) {
			/* fall through to the link */
		}
	});
}
